import { Inject, Injectable } from "@angular/core";
import { createEntityStore, EntityState, EntityStore, persistState } from "@datorama/akita";
import { BehaviorSubject } from "rxjs";
import { LocalStorageService } from "@services/localstorage.service";
import { akitaPersistStorage, isPersistedSnapshotLoaded } from '@persistence/akita-persist-storage';
import { STORAGE_INFO_KEY, StorageInfo } from "@ep-om/app.initializer";
import { CrudService } from "../crudService";
import { IBaseInterfaceData } from "@common/interfaces/base";
import { AbstractInterfaceDataQuery } from "../interfaceDataModel";
import { CrudServiceBuilder } from "../crudServiceBuilder";
import { ID } from "@common/interfaces/id";
import { canRuntimePersist } from '@persistence/session-cache';

@Injectable({
  providedIn: 'root'
})
export class MetaInterfaceServiceProvider {
  private _services: { [storename: string]: CrudService<IBaseInterfaceData, EntityStore<EntityState<IBaseInterfaceData, ID>>, AbstractInterfaceDataQuery<IBaseInterfaceData>> } = {}
  private _storeNames$ = new BehaviorSubject<string[]>([]);
  public storeNames$ = this._storeNames$.asObservable();
  // Store per cui e' stata creata una persistState (vedi loadStores). Consultato da
  // PersistedStoreRegistry per classificare i cursori droppati da reconcilePersistedCursors.
  private _persistedStoreNames = new Set<string>();

  constructor(
    private localStorage: LocalStorageService,
    @Inject(STORAGE_INFO_KEY) private storageInfoKey: string,
    private crudServiceBuilder: CrudServiceBuilder,
  ) {}

  loadStores(_stores: {name: string, idKey?: string}[]) {
    const stores = _stores.filter(store => !this._services[store.name]);
    if (stores.length && !isPersistedSnapshotLoaded()) {
      // Senza snapshot in memoria getItem e' asincrona e l'aggancio di persistState torna a dipendere
      // dal replay di $$addStore (50 store / 5s): succede solo se il gate di idratazione di main.ts e'
      // scaduto o la lettura di IDB e' fallita. Lo segnaliamo perche' lo store potrebbe non persistere.
      console.warn('[META INTERFACES] snapshot IDB non in memoria: persistenza degli store meta non garantita', stores.map((store) => store.name));
    }
    for (const store of stores) {
      // ORDINE OBBLIGATO: prima persistState, poi lo store. Con lo snapshot in memoria
      // akitaPersistStorage.getItem risponde in modo sincrono, quindi persistState sottoscrive
      // $$addStore gia' dentro questa chiamata e riceve live la creazione dello store che segue.
      // Nessuna dipendenza dal replay (50 store / 5s), che e' cio' che lasciava alcuni store meta
      // mai idratati e mai persistiti (creati durante il first-sync con IDB congestionato, o piu'
      // di 50 in un colpo). Lo store resta disponibile in modo sincrono per chi chiama addStore.
      persistState({
        key: store.name,
        storage: akitaPersistStorage,
        include: [store.name]
      });
      this._persistedStoreNames.add(store.name);
      const entityStore = createEntityStore<EntityState<IBaseInterfaceData, ID>>([], {
        name: store.name,
        idKey: store.idKey || 'id',
      });
      const query = new (this.crudServiceBuilder.buildQuery())(entityStore);
      const service = new (this.crudServiceBuilder.buildService(entityStore, query))();
      this._services[store.name] = service;
    }
    const storeNames = Object.keys(this._services);
    this._storeNames$.next(storeNames);
  }

  addStore(store: {name: string, idKey?: string}) {
    console.log('adding store', store.name);
    this.loadStores([store]);
    if (canRuntimePersist()) {
      this.localStorage.updateInTransaction<StorageInfo[]>(
        this.storageInfoKey,
        (currentInfo) => {
          // La chiave puo' essere assente: la rimuove il recupero dell'initializer e la azzera il
          // reset di MetaInterfacesStore.
          currentInfo = currentInfo || [];
          if (!currentInfo.some(currentStore => currentStore.name === store.name)) {
            currentInfo.push({
              name: store.name,
              isMeta: true,
              idKey: store.idKey,
            });
          }
          return currentInfo;
        }
      )
    }
  }
  
  removeStore(storeNameToBeRemoved: string) {
    const storeNames = Object.keys(this._services);
    const remainingStoreNames = storeNames.filter(name => name !== storeNameToBeRemoved);
    if (canRuntimePersist()) {
      this.localStorage.updateInTransaction<StorageInfo[]>(
        this.storageInfoKey,
        (currentInfo) => {
          return currentInfo?.filter(store => store.name !== storeNameToBeRemoved);
        }
      );
    }
    this._storeNames$.next(remainingStoreNames);
  }

  /** true se per lo store meta e' stata creata una persistState (non implica che sia gia' stato scritto su IDB). */
  isPersistedStore(name: string): boolean {
    return this._persistedStoreNames.has(name);
  }

  /** true se lo store dinamico e' registrato (creato in questa sessione via loadStores/addStore). */
  hasStore(name: string): boolean {
    return !!this._services[name];
  }

  /**
   * true se l'interfaccia e' utilizzabile: le interfacce statiche lo sono sempre (query/service
   * risolti via interfaceDataRegistry), quelle meta solo se lo store e' registrato.
   * Un'interfaccia meta non disponibile va trattata come assente, mai dereferenziata.
   */
  isInterfaceAvailable(interfaceName: string): boolean {
    return !interfaceName.startsWith('_meta_') || this.hasStore(interfaceName);
  }

  /**
   *
   * @param name is the name of the store - aka the interface value
   * @returns CrudService
   */
  getService(name: string) {
    return this._services[name];
  }

  /**
   * 
   * @param name is the name of the store - aka the interface value
   * @returns AbstractInterfaceDataQuery
   */
  getQuery(name: string) {
    return this._services[name]?.query;
  }
}
